import React from 'react';
import classnames from 'classnames';

interface IProgressBarProps {
  completed: number;
  total: number;
  height?: number;
  backgroundColor?: string;
  backgroundOpacity?: number;
  fillColor?: string;
  ref?: React.RefObject<ProgressBar>;
  animation?: boolean;
  fakeProgress?: boolean;
  icon?: React.ReactNode;
}

interface IProgressBarWrapperProps {
  total: number;
  height?: number;
  children?: React.ReactNode;
}

/** @deprecated - prefer `design-system/LinearProgress` */
export class ProgressBarWrapper extends React.Component<IProgressBarWrapperProps> {
  static defaultProps = {
    total: 1,
    height: 8,
  };

  render() {
    const { total, height } = this.props;

    return (
      <div className="progress-container">
        {React.Children.map(this.props.children, child => {
          if (!child) return null;
          return React.cloneElement(child as any, { total });
        })}

        <style jsx>{`
          .progress-container {
            width: 100%;
            position: relative;
            height: ${height}px;
          }
        `}</style>
      </div>
    );
  }
}

interface IBarProps {
  completed: number;
  fillColor?: string;
  animation?: boolean;
  total?: number;
  icon?: React.ReactNode;
}

export class Bar extends React.Component<IBarProps> {
  static defaultProps = {
    completed: 0,
    total: 1,
    fillColor: 'var(--color-RemoGreen)',
    animation: true,
  };

  render() {
    const { completed, total, fillColor, animation, icon } = this.props;
    const width = Math.max(Math.min(completed / total!, 1) * 100, 0.5);

    return (
      <React.Fragment>
        <div
          className={classnames('progress-fill', { animation })}
          style={{ width: `${width}%` }}
        />
        {icon && (
          <div
            className={classnames('icon-container', { animation })}
            style={{
              position: 'absolute',
              left: `${width + 10}%`, // 10% padding between icon and progress bar
              transition: 'left 1s ease-in-out',
              transform: 'translateX(-50%)', // Centers the icon horizontally at the position
            }}
          >
            {icon}
          </div>
        )}

        <style jsx>{`
          .progress-fill {
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            background: ${fillColor};
            border-radius: 100px;
            width: 0;
            min-height: 8px; /* Ensure consistent height for border-radius */
            min-width: 8px; /* Match height for perfect circle at start */
          }

          .progress-fill.animation {
            transition: width 1s ease-in-out;
          }

          .icon-container {
            top: -50%; /* Center vertically */
          }

          .icon-container.animation {
            animation: tilt 1s ease-in-out infinite;
          }

          @keyframes tilt {
            0% {
              transform: translateX(-50%) rotate(0deg);
            }
            25% {
              transform: translateX(-50%) rotate(15deg); /* Tilt clockwise */
            }
            50% {
              transform: translateX(-50%) rotate(-15deg); /* Tilt counterclockwise */
            }
            75% {
              transform: translateX(-50%) rotate(15deg); /* Tilt clockwise */
            }
            100% {
              transform: translateX(-50%) rotate(0deg);
            }
          }
        `}</style>
      </React.Fragment>
    );
  }
}

interface IBarTrackProps {
  backgroundColor?: string;
  backgroundOpacity?: number;
}

export class BarTrack extends React.Component<IBarTrackProps> {
  static defaultProps = {
    backgroundColor: 'var(--color-RemoGray05)',
    backgroundOpacity: 1,
  };

  render() {
    const { backgroundColor, backgroundOpacity } = this.props;

    return (
      <React.Fragment>
        <div className="progress-track" />

        <style jsx>{`
          .progress-track {
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            background: ${backgroundColor};
            opacity: ${backgroundOpacity};
            border-radius: 100px;
          }
        `}</style>
      </React.Fragment>
    );
  }
}

class ProgressBar extends React.Component<IProgressBarProps> {
  render() {
    const {
      completed,
      total,
      height,
      backgroundColor,
      backgroundOpacity,
      fillColor,
      animation,
      icon,
    } = this.props;

    return (
      <ProgressBarWrapper total={total} height={height}>
        <BarTrack backgroundColor={backgroundColor} backgroundOpacity={backgroundOpacity} />
        <Bar animation={animation} fillColor={fillColor} completed={completed} icon={icon} />
      </ProgressBarWrapper>
    );
  }
}

export default ProgressBar;
